/**
 * Modal para crear/editar Clase Progresiva
 */

import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button, Input } from "@/components/ui";
import { X, Loader2 } from "lucide-react";
import {
  useCreateProgressClass,
  useUpdateProgressClass,
} from "@/hooks/useCatalogs";
import type { ProgressClass, ClubType } from "@/lib/services/catalogs";

const progressClassSchema = z.object({
  name: z.string().min(3, "El nombre debe tener al menos 3 caracteres"),
  code: z.string().min(2, "El código debe tener al menos 2 caracteres"),
  clubType: z.enum(["aventureros", "conquistadores", "guias_mayores"]),
  order: z.coerce.number().min(1, "El orden debe ser mayor a 0"),
  minAge: z.coerce.number().min(1, "La edad mínima es requerida"),
  maxAge: z.coerce.number().optional(),
  description: z.string().optional(),
});

type ProgressClassForm = z.infer<typeof progressClassSchema>;

interface ProgressClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  progressClass?: ProgressClass | null;
}

const clubTypeOptions: { value: ClubType; label: string }[] = [
  { value: "aventureros", label: "Aventureros" },
  { value: "conquistadores", label: "Conquistadores" },
  { value: "guias_mayores", label: "Guías Mayores" },
];

export default function ProgressClassModal({
  isOpen,
  onClose,
  progressClass,
}: ProgressClassModalProps) {
  const isEditing = !!progressClass;

  const createMutation = useCreateProgressClass();
  const updateMutation = useUpdateProgressClass();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ProgressClassForm>({
    resolver: zodResolver(progressClassSchema),
    defaultValues: {
      name: progressClass?.name || "",
      code: progressClass?.code || "",
      clubType: progressClass?.clubType || "conquistadores",
      order: progressClass?.order || 1,
      minAge: progressClass?.minAge || 10,
      maxAge: progressClass?.maxAge,
      description: progressClass?.description || "",
    },
  });

  // Reset form when progressClass changes
  useEffect(() => {
    if (isOpen) {
      reset({
        name: progressClass?.name || "",
        code: progressClass?.code || "",
        clubType: progressClass?.clubType || "conquistadores",
        order: progressClass?.order || 1,
        minAge: progressClass?.minAge || 10,
        maxAge: progressClass?.maxAge,
        description: progressClass?.description || "",
      });
    }
  }, [progressClass, isOpen, reset]);

  const onSubmit = async (data: ProgressClassForm) => {
    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          id: progressClass.id,
          data: data as Partial<ProgressClass>,
        });
      } else {
        await createMutation.mutateAsync({
          ...data,
          status: "active",
        });
      }
      onClose();
    } catch (error) {
      // Error handled by mutation
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? "Editar Clase" : "Nueva Clase"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            label="Nombre *"
            {...register("name")}
            error={errors.name?.message}
            placeholder="Ej: Explorador"
          />

          <Input
            label="Código *"
            {...register("code")}
            error={errors.code?.message}
            placeholder="Ej: EXP"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Club *
            </label>
            <select
              {...register("clubType")}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
            >
              {clubTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {errors.clubType && (
              <p className="text-sm text-red-600 mt-1">
                {errors.clubType.message}
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Input
              label="Orden *"
              type="number"
              {...register("order")}
              error={errors.order?.message}
            />
            <Input
              label="Edad mín *"
              type="number"
              {...register("minAge")}
              error={errors.minAge?.message}
            />
            <Input
              label="Edad máx"
              type="number"
              {...register("maxAge")}
              error={errors.maxAge?.message}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción
            </label>
            <textarea
              {...register("description")}
              rows={3}
              className="w-full px-3 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-4 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-300"
              placeholder="Descripción opcional..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading} className="flex-1">
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : isEditing ? (
                "Guardar"
              ) : (
                "Crear"
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
